declare global {
  interface Window {
    // 仕様書: game_start({callback: コールバック関数名}) または game_start(コールバック関数)
    game_start?: ((callback: (() => void) | string) => void) | ((args: { callback: (() => void) | string }) => void);
    // 仕様書: game_end({callback: コールバック関数名, score: スコア数値})
    // 注意: GMO APIがエラーを返す場合、コールバックが呼ばれない可能性がある
    game_end?: (args: { callback?: (() => void) | string; score: number }) => void;
    // 仕様書: game_save({callback: コールバック関数名, data: json形式の値})
    game_save?: (args: { callback?: (() => void) | string; data: string }) => void;
    // 仕様書: game_get_data({callback: コールバック関数名})
    game_get_data?: (args: { callback: ((data: { gamedata?: string }) => void) | string }) => void;
  }
}

export interface GameSaveData {
  currentStage: number;
  puzzleKeys: string[];
  clearedStages: number[];
  gameMode: "score" | "free";
  puzzleSize?: 2 | 3 | 4 | 5 | 6; // FREE MODE用のパズルサイズ
  completedSizes?: (2 | 3 | 4 | 5 | 6)[]; // 全クリしたサイズの配列
}

export class GameBox {

  /**
   * ゲーム開始時実行機能（必須）
   * ゲームを開始する際にコールするAPI
   * 仕様書: game_start(コールバック関数) または game_start({callback: コールバック関数名})
   * サンプルコードでは直接関数を渡す形式が使われている
   */
  static start(onStarted: () => void) {
    console.log("[GameBox] game_start呼び出し", new Error().stack?.split('\n').slice(0, 5).join('\n'));
    
    // コールバックが実行されたかどうかを追跡
    let callbackExecuted = false;
    let timeoutId: number | null = null;
    
    const wrappedCallback = () => {
      if (callbackExecuted) {
        console.warn("[GameBox] game_start コールバックが既に実行済み - 無視");
        return;
      }
      callbackExecuted = true;
      
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      console.log("[GameBox] game_start()のコールバックが実行されました");
      onStarted();
    };
    
    if (typeof window.game_start === "function") {
      // タイムアウトを設定（3秒）
      timeoutId = window.setTimeout(() => {
        if (!callbackExecuted) {
          console.warn("[GameBox] game_start タイムアウト - コールバックを強制実行");
          wrappedCallback();
        }
      }, 3000);
      
      // 仕様書のサンプルコードに従い、直接関数を渡す形式を優先
      // 形式1: game_start(コールバック関数) - 仕様書のサンプルで使用
      const fn = window.game_start as (callback: (() => void) | string) => void;
      try {
        fn(wrappedCallback);
        return;
      } catch (e) {
        // 形式1が失敗した場合、オブジェクト形式を試す
        // 形式2: game_start({callback: コールバック関数名})
        try {
          const fn2 = window.game_start as (args: { callback: (() => void) | string }) => void;
          fn2({ callback: wrappedCallback });
          return;
        } catch (e2) {
          console.error("[GameBox] game_start呼び出しエラー:", e2);
          // エラー時はタイムアウトをクリアして即座に実行
          if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          wrappedCallback();
          return;
        }
      }
    }
    // ローカル開発時はそのまま実行
    wrappedCallback();
  }

  /**
   * ゲーム終了時実行機能（必須）
   * ゲームを終了しスコアを送信する際に使用するAPI
   * 注意: GMO APIがコールバックを返さない場合でも、タイムアウトで処理を続行する
   * 注意: GMO APIがエラーを返す場合、エラーモーダルを表示させないようにする
   */
  static end(score: number, onEnded?: () => void) {
    console.log("[GameBox] game_end呼び出し - スコア:", score, new Error().stack?.split('\n').slice(0, 5).join('\n'));
    
    // コールバックが一度だけ実行されるようにフラグを追加
    let callbackExecuted = false;
    let timeoutId: number | null = null;
    
    const wrappedCallback = () => {
      // 既に実行済みの場合は無視
      if (callbackExecuted) {
        console.warn("[GameBox] game_end コールバックが既に実行済み - 無視");
        return;
      }
      callbackExecuted = true;
      
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      onEnded?.();
    };

    if (typeof window.game_end === "function") {
      try {
        // タイムアウトを設定（3秒に短縮 - GMO APIの応答が遅い場合でも、エラーモーダルを表示させない）
        // 短いタイムアウトで、GMO APIがエラーを返す前にコールバックを強制実行する
        timeoutId = window.setTimeout(() => {
          if (!callbackExecuted) {
            console.warn("[GameBox] game_end タイムアウト - コールバックを強制実行（GMO APIが応答しないため）");
            wrappedCallback();
          }
        }, 3000);

        // GMO APIを呼び出す前に、エラーハンドリングを設定
        try {
          // スコアが0の場合は1を送信（GMOプラットフォーム側で0を受け取るとエラーになる可能性があるため）
          // Score Modeでは通常0になることはないが、念のため最小値1を保証
          const finalScore = score > 0 ? score : 1;
          window.game_end({ 
            score: finalScore,
            callback: wrappedCallback 
          });
        } catch (apiError) {
          // API呼び出し自体がエラーの場合（GMO APIがエラーを返す可能性）
          console.error("[GameBox] game_end API呼び出しエラー:", apiError);
          if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          // エラー時でもコールバックを実行してゲームを続行できるようにする
          // エラーモーダルを表示させないため、即座にコールバックを実行
          if (!callbackExecuted) {
            callbackExecuted = true;
            onEnded?.();
          }
        }
        return;
      } catch (e) {
        console.error("[GameBox] game_end呼び出しエラー:", e);
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        // エラー時でもコールバックを実行してゲームを続行できるようにする
        if (!callbackExecuted) {
          callbackExecuted = true;
          onEnded?.();
        }
        return;
      }
    }
    // ローカル開発時: GMO APIが存在しない場合はそのまま実行
    onEnded?.();
  }

  /**
   * ゲームデータ同期機能
   * ゲーム内のデータを保存するAPI
   * 保存容量は1024文字が目安
   * game_endをコールするまでの1ゲーム内に1回のみ
   * 注意: GMO APIがエラーを返す場合、エラーモーダルを表示させないようにする
   */
  static save(data: GameSaveData, onSaved?: () => void) {
    console.log("[GameBox] game_save呼び出し - Stage:", data.currentStage, "Cleared:", data.clearedStages.length);
    
    // コールバックが一度だけ実行されるようにフラグを追加
    let callbackExecuted = false;
    let timeoutId: number | null = null;
    
    const wrappedCallback = () => {
      // 既に実行済みの場合は無視
      if (callbackExecuted) {
        console.warn("[GameBox] game_save コールバックが既に実行済み - 無視");
        return;
      }
      callbackExecuted = true;
      
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      onSaved?.();
    };

    try {
      const jsonData = JSON.stringify(data);
      
      // データサイズを確認（1024文字制限 - エラーモーダルを表示させないため）
      if (jsonData.length > 1024) {
        console.error("[GameBox] game_save - データサイズが1024文字を超えています:", jsonData.length);
        // エラーモーダルを表示させないため、APIを呼ばずに即座にコールバックを実行
        wrappedCallback();
        return;
      }
      
      if (typeof window.game_save === "function") {
        // タイムアウトを設定（10秒 - GMO APIの応答を待つ）
        timeoutId = window.setTimeout(() => {
          if (!callbackExecuted) {
            console.warn("[GameBox] game_save タイムアウト - コールバックを強制実行");
            wrappedCallback();
          }
        }, 10000);

        // GMO APIを呼び出す（データサイズは1024文字以下が保証されている）
        window.game_save({
          callback: wrappedCallback,
          data: jsonData,
        });
        return;
      }
      // ローカル開発時: GMO APIが存在しない場合はそのまま実行
      onSaved?.();
    } catch (e) {
      console.error("[GameBox] game_save呼び出しエラー:", e);
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      // エラー時でもコールバックを実行してゲームを続行できるようにする
      if (!callbackExecuted) {
        callbackExecuted = true;
        onSaved?.();
      }
    }
  }

  /**
   * ゲームデータ取得機能
   * 保存されたゲーム内のデータを取得する関数
   * ゲーム開始の前に実行する
   */
  static getData(onData: (data: GameSaveData | null) => void) {
    console.log("[GameBox] game_get_data呼び出し");
    
    // コールバックが一度だけ実行されるようにフラグを追加
    let callbackExecuted = false;
    let timeoutId: number | null = null;
    
    const wrappedCallback = (response: { gamedata?: string }) => {
      // 既に実行済みの場合は無視
      if (callbackExecuted) {
        console.warn("[GameBox] game_get_data コールバックが既に実行済み - 無視");
        return;
      }
      callbackExecuted = true;
      
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      if (response.gamedata) {
        try {
          const parsed = JSON.parse(response.gamedata) as GameSaveData;
          console.log("[GameBox] game_get_data取得完了 - Stage:", parsed.currentStage, "Cleared:", parsed.clearedStages.length);
          onData(parsed);
        } catch (e) {
          console.error("[GameBox.getData] パースエラー:", e);
          onData(null);
        }
      } else {
        console.log("[GameBox] game_get_data - データなし");
        onData(null);
      }
    };

    // GMOプラットフォーム: 実際のAPIを使用
    if (typeof window.game_get_data === "function") {
      try {
        // タイムアウトを設定（10秒に延長）
        timeoutId = window.setTimeout(() => {
          if (!callbackExecuted) {
            console.warn("[GameBox] game_get_data タイムアウト - nullを返す");
            callbackExecuted = true;
            onData(null);
          }
        }, 10000);

        window.game_get_data({
          callback: wrappedCallback,
        });
        return;
      } catch (e) {
        console.error("[GameBox] game_get_data呼び出しエラー:", e);
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        // エラー時はnullを返す
        if (!callbackExecuted) {
          callbackExecuted = true;
          onData(null);
        }
        return;
      }
    }

    // ローカル開発時: GMO APIが存在しない場合はnullを返す
    console.log("[GameBox] game_get_data - ローカル開発モード（データなし）");
    onData(null);
  }
}
